import { useEffect, useState, KeyboardEvent } from 'react';
import {
  Modal,
  Backdrop,
  Box,
  Typography,
  Chip,
  TextField,
} from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import CancelIcon from '@material-ui/icons/Close';

import { useTextStyles } from 'src/hooks/useTextStyles';
import Button from 'src/components/NewButton';
import useNotification from 'src/hooks/useNotification';
import useAuth from 'src/hooks/useAuth';
import { createShares } from 'src/apis/share';
import { ShareResourceType, toShareResourceTypeApi } from 'src/utils/share';

export type { ShareResourceType };

export interface ShareTarget {
  type: ShareResourceType;
  name: string;
  resourceId: string;
}

interface ShareModalProps {
  open: boolean;
  target: ShareTarget | null;
  closeModal: () => void;
}

const RESOURCE_LABELS: Record<ShareResourceType, string> = {
  dashboard: 'Dashboard',
  dashboardFolder: 'Dashboard Folder',
  campaign: 'Campaign',
  campaignFolder: 'Campaign Folder',
};

const useStyles = makeStyles(() =>
  createStyles({
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 16,
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
      marginTop: 8,
    },
    resourceName: {
      color: '#667085',
      marginBottom: 4,
    },
    pills: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 8,
      minHeight: 0,
    },
    pill: {
      backgroundColor: '#EFF8FF',
      color: '#175CD3',
      fontWeight: 500,
    },
    inputField: {
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
    },
    input: {
      padding: '6px 8px',
      borderRadius: 8,
      border: '1px solid #C3C3C7',
      width: '100%',
      '& .MuiInputBase-root:before, & .MuiInputBase-root:after': {
        border: 'none !important',
      },
    },
    helperText: {
      color: '#667085',
      fontSize: 12,
    },
    buttons: {
      paddingTop: 16,
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      '& button': {
        flex: 1,
      },
    },
  })
);

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '100%',
  maxWidth: 480,
  bgcolor: 'background.paper',
  background: '#fff',
  borderRadius: 8,
  boxShadow:
    '0px 8px 8px -4px rgba(16, 24, 40, 0.03), 0px 20px 24px -4px rgba(16, 24, 40, 0.08)',
  padding: 24,
};

export const ShareModal = ({ open, target, closeModal }: ShareModalProps) => {
  const classes = useStyles();
  const textClasses = useTextStyles();
  const { handleSuccess, handleError } = useNotification();
  const { user } = useAuth();

  const [organizationIds, setOrganizationIds] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setOrganizationIds([]);
      setInputValue('');
      setLoading(false);
    }
  }, [open, target]);

  const addOrganizationId = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    if (organizationIds.includes(trimmed)) {
      setInputValue('');
      return;
    }
    if (trimmed === user?.organizationId) {
      handleError(null, 'You cannot share with your own organization.');
      setInputValue('');
      return;
    }
    if (organizationIds.length >= 100) {
      handleError(
        null,
        'You can share with at most 100 organizations at once.'
      );
      return;
    }
    setOrganizationIds((prev) => [...prev, trimmed]);
    setInputValue('');
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      addOrganizationId();
    }
  };

  const handleRemove = (id: string) => {
    setOrganizationIds((prev) => prev.filter((item) => item !== id));
  };

  const handleShare = async () => {
    if (!target || loading || !organizationIds.length) return;

    if (organizationIds.length > 100) {
      handleError(
        null,
        'You can share with at most 100 organizations at once.'
      );
      return;
    }

    setLoading(true);
    try {
      const result = await createShares({
        resourceType: toShareResourceTypeApi(target.type),
        resourceId: target.resourceId,
        organizationIds,
      });

      const createdCount = result.created?.length ?? 0;
      const alreadyCount = result.alreadyShared?.length ?? 0;
      const label =
        result.resourceName || target.name || RESOURCE_LABELS[target.type];

      if (createdCount && alreadyCount) {
        handleSuccess(
          `${label} shared with ${createdCount} organization(s). ${alreadyCount} already had access.`
        );
      } else if (createdCount) {
        handleSuccess(`${label} shared successfully.`);
      } else if (alreadyCount) {
        handleSuccess(
          `${label} was already shared with the selected organization(s).`
        );
      } else {
        handleSuccess(`${label} shared successfully.`);
      }

      closeModal();
    } catch (error) {
      handleError(
        error,
        'Unable to share. Please check the organization IDs and try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const canShare = organizationIds.length > 0;
  const title = target ? `Share ${RESOURCE_LABELS[target.type]}` : 'Share';

  return (
    <Modal
      open={open}
      onClose={loading ? undefined : closeModal}
      BackdropComponent={Backdrop}
      disableEnforceFocus
    >
      <Box sx={modalStyle}>
        <Box className={classes.header}>
          <Typography
            className={textClasses.lgBold}
            style={{ color: '#101828' }}
          >
            {title}
          </Typography>
          <CancelIcon
            style={{
              cursor: loading ? 'default' : 'pointer',
              color: '#667085',
            }}
            onClick={loading ? undefined : closeModal}
          />
        </Box>

        {target?.name ? (
          <Typography
            className={`${textClasses.smRegular} ${classes.resourceName}`}
          >
            {target.name}
          </Typography>
        ) : null}

        <Box className={classes.content}>
          <Box className={classes.inputField}>
            <Typography className={textClasses.smMedium}>
              Organization IDs
            </Typography>
            {organizationIds.length > 0 ? (
              <Box className={classes.pills}>
                {organizationIds.map((id) => (
                  <Chip
                    key={id}
                    label={id}
                    onDelete={loading ? undefined : () => handleRemove(id)}
                    className={classes.pill}
                    size='small'
                  />
                ))}
              </Box>
            ) : null}
            <TextField
              className={classes.input}
              placeholder='Enter organization ID and press Enter'
              value={inputValue}
              onChange={(event) => setInputValue(event.target.value)}
              onKeyDown={handleKeyDown}
              fullWidth
              disabled={loading}
            />
            <Typography className={classes.helperText}>
              Enter one organization ID at a time. Press Enter to confirm each
              ID.
            </Typography>
          </Box>
        </Box>

        <Box className={classes.buttons}>
          <Button
            title='Cancel'
            variant='outlined'
            height={40}
            disabled={loading}
            onClick={closeModal}
          />
          <Button
            title={loading ? 'Sharing…' : 'Share'}
            color='primary'
            height={40}
            disabled={!canShare || loading}
            onClick={handleShare}
          />
        </Box>
      </Box>
    </Modal>
  );
};
